/**
 * The drone (owner, Sep 25: "new item: drone. you can fly it and see like you were flying it, but the player stays in the spot
 * where it flies. it needs to have a hover and "fly to destination" (set in the map) so you can switch between the drone view
 * and your own view. So you can set it to fly somewhere and play while it flies there. slower than helicopters or planes").
 *
 * An item with `flies` (content/items/drone.json) takes off as a vehicle of class `drone` (content/vehicles/drone.json) beside
 * its owner. Nobody sits in it: `VehicleEntity.drone` says whose it is and what it is doing, and `stepDrone` below flies it —
 * a quadcopter, so the stick is a velocity in the world's axes (the view is top-down) and letting go is a hover, not a glide.
 * The owner's sticks reach it only while their browser is in the drone's view (`InputState.droneFly`); otherwise it does what it
 * was told: hover, fly to the map's destination at cruise height (climbing over what is in the way), come back and land beside
 * its owner, or land where it is. The battery is the content's `stats.range` in minutes; out of it, the drone comes down where
 * it is and has to be walked to. Pure over the world, so the server, the local game and the client's prediction share it.
 * docs/DESIGN_DRONE.md.
 */
import { vehicleRange } from '../content/types';
import type { WorldStream } from '../engine/world-stream';
import { isWater } from './physics';
import type { DroneMode, VehicleEntity } from './entities';
export { DRONE_MODES } from './entities';

/** the autopilot's height (m): over most of a town's roofs */
export const DRONE_CRUISE = 30;
/** where a take-off stops and waits (m) */
export const DRONE_LAUNCH = 6;
/** close enough to a destination (m, level distance) */
export const DRONE_ARRIVE = 2;
/** a drone that lands this close to its owner goes straight back into the pockets (m) */
export const DRONE_REACH = 4;
/** battery below this: the owner is told to bring it back */
export const DRONE_LOW = 0.2;
/** the least charge a take-off needs */
export const DRONE_MIN = 0.1;
/** (Sep 25 follow-up) the police notice a drone lower than this (m) with an officer within DRONE_COP_R (m), twice that over a body or a wreck */
export const DRONE_COP_ALT = 20, DRONE_COP_R = 18;
/** seconds in the backpack from flat to full */
export const DRONE_CHARGE_S = 150;
export const DRONE_LABEL: Record<DroneMode, string> = { hover: 'Hovering', rise: 'Taking off', goto: 'Flying to the destination', return: 'Coming back to you', land: 'Landing', flat: 'Battery flat — coming down' };

/** the sticks on the drone: a direction in the world's axes (−1..1 each, +y = south, as on screen) and the climb keys */
export interface DroneStick { x: number; y: number; up: boolean; down: boolean }
export type DroneEvent = 'landed' | 'water' | 'arrived' | 'low' | 'flat' | 'hit';

/** a building's height at a tile, not a tree's or a lamp's (what a flyer bumps into; physics.ts `tileBlocked` 'air' says the same) */
function roofAt(w: WorldStream, x: number, y: number): number { if (!w.loaded(x, y)) return 0; const h = w.blockH(x, y); return h > 0 && h !== w.decorH(x, y) ? h : 0; }
/** a spot the drone may not enter at this height: an unstreamed chunk, or a building taller than it flies */
function walled(w: WorldStream, x: number, y: number, alt: number): boolean { if (!w.loaded(x, y)) return true; return roofAt(w, x, y) > alt; }
const R = 0.3; // the frame's half width (m): the four arms' tips
function blockedAt(w: WorldStream, x: number, y: number, alt: number): boolean { return walled(w, x, y, alt) || walled(w, x - R, y - R, alt) || walled(w, x + R, y - R, alt) || walled(w, x - R, y + R, alt) || walled(w, x + R, y + R, alt); }

/** the nearest open ground (no building on it, not water) within `reach` tiles, in rings — where a drone over a roof goes down */
export function openGround(w: WorldStream, x: number, y: number, reach = 16): [number, number] | undefined {
  const ok = (px: number, py: number) => w.loaded(px, py) && roofAt(w, px, py) === 0 && !isWater(w, px, py) && !!w.terrainAt(px, py)?.walkable;
  if (ok(x, y)) return [x, y];
  for (let r = 1; r <= reach; r++) for (let i = -r; i <= r; i++) for (const [px, py] of [[x + i, y - r], [x + i, y + r], [x - r, y + i], [x + r, y + i]] as [number, number][]) if (ok(px, py)) return [Math.floor(px) + 0.5, Math.floor(py) + 0.5];
  return undefined;
}

/**
 * One step of a drone in the air. `stick` is its owner's sticks while they look through its camera (undefined otherwise); `home`
 * is where its owner stands (for Return). A stick that is pushed takes it off any autopilot and flies it by hand; let go, it
 * hovers. Returns what happened worth saying: it touched down (on land or in water), reached its destination, the battery went
 * low or flat, or it hit a wall hard.
 */
export function stepDrone(w: WorldStream, v: VehicleEntity, stick: DroneStick | undefined, home: { x: number; y: number } | undefined, dt: number): DroneEvent | undefined {
  const d = v.drone!, st = v.def.stats, fl = v.def.flight ?? { maxAltitude: 120, climbRate: 4, stallSpeed: 0 };
  let ev: DroneEvent | undefined;
  if (v.wrecked && d.mode !== 'flat') d.mode = 'flat';
  if (d.mode !== 'flat') {
    const was = d.bat;
    d.bat = Math.max(0, d.bat - dt / (vehicleRange(v.def) * 60));
    if (was > DRONE_LOW && d.bat <= DRONE_LOW) ev = 'low';
    if (d.bat <= 0) { d.mode = 'flat'; ev = 'flat'; }
  }
  const pushed = !!stick && (Math.abs(stick.x) > 0.08 || Math.abs(stick.y) > 0.08 || stick.up || stick.down);
  if (pushed && d.mode !== 'flat') { d.mode = 'hover'; d.tx = d.ty = undefined; }
  const max = st.maxSpeed, climb = fl.climbRate, under = roofAt(w, v.x, v.y);
  let wx = 0, wy = 0, wz = 0; // the velocity it wants, and its climb rate
  // somewhere to come down that is not a roof or the water (landing, or out of battery over a building or a lake): drift to the nearest open ground
  const offRoof = (speed: number): boolean => { if (under <= 0 && !isWater(w, v.x, v.y)) return false; const g = openGround(w, v.x, v.y, 24); if (!g) return false; const dx = g[0] - v.x, dy = g[1] - v.y, m = Math.hypot(dx, dy) || 1; wx = (dx / m) * speed; wy = (dy / m) * speed; return true; };
  if (d.mode === 'flat') { wz = -climb * 0.6; if (!offRoof(3)) { wx = d.vx * 0.5; wy = d.vy * 0.5; } }
  else if (pushed) { const m = Math.hypot(stick!.x, stick!.y), k = m > 1 ? 1 / m : 1; wx = stick!.x * k * max; wy = stick!.y * k * max; wz = stick!.up ? climb : stick!.down ? -climb : 0; }
  else if (d.mode === 'rise') { wz = climb; if (v.alt >= DRONE_LAUNCH) { d.mode = 'hover'; wz = 0; } }
  else if (d.mode === 'land') { if (!offRoof(4)) wz = -climb; else if (v.alt > under + 2) wz = -climb; }
  else if (d.mode === 'goto' || d.mode === 'return') {
    const tx = d.mode === 'return' ? home?.x : d.tx, ty = d.mode === 'return' ? home?.y : d.ty;
    if (tx === undefined || ty === undefined) d.mode = 'hover';
    else {
      const dx = tx - v.x, dy = ty - v.y, dist = Math.hypot(dx, dy);
      if (dist < DRONE_ARRIVE && Math.hypot(d.vx, d.vy) < 1.2) { if (d.mode === 'return') d.mode = 'land'; else { d.mode = 'hover'; d.tx = d.ty = undefined; ev = 'arrived'; } }
      else {
        // at the cruise height (or whatever it was flown up to), braking so it arrives rather than overshoots
        const sp = Math.min(max, Math.sqrt(2 * st.brake * 0.6 * dist)), ux = dx / dist, uy = dy / dist;
        const look = 3 + Math.hypot(d.vx, d.vy) * 0.9;
        let ahead = 0; for (const f of [0.35, 0.7, 1]) ahead = Math.max(ahead, roofAt(w, v.x + ux * look * f, v.y + uy * look * f));
        const clearance = Math.max(ahead, under) + 3;
        if (clearance > v.alt) { wz = climb; wx = ux * Math.min(sp, 2); wy = uy * Math.min(sp, 2); } // something tall ahead: go up and over, creeping
        else { wx = ux * sp; wy = uy * sp; if (v.alt < DRONE_CRUISE - 0.5) wz = climb; }
      }
    }
  }
  // (hover: all zero — it holds where it is)
  // towards the wanted velocity at the frame's acceleration
  const ax = wx - d.vx, ay = wy - d.vy, am = Math.hypot(ax, ay), acc = (d.mode === 'flat' ? 2 : st.accel) * dt;
  if (am <= acc) { d.vx = wx; d.vy = wy; } else { d.vx += (ax / am) * acc; d.vy += (ay / am) * acc; }
  // up and down first (a roof is a floor), then along, with the buildings in the way (trees and lamps it flies through)
  const floor = roofAt(w, v.x, v.y);
  v.alt = Math.max(Math.min(fl.maxAltitude, v.alt + wz * dt), floor > 0 ? floor + 0.3 : 0);
  const nx = v.x + d.vx * dt, ny = v.y + d.vy * dt, speed = Math.hypot(d.vx, d.vy);
  if (!blockedAt(w, nx, ny, v.alt)) { v.x = nx; v.y = ny; }
  else {
    const okX = !blockedAt(w, nx, v.y, v.alt), okY = !blockedAt(w, v.x, ny, v.alt);
    if (okX) v.x = nx; else d.vx = 0;
    if (okY) v.y = ny; else d.vy = 0;
    if (!okX && !okY) { d.vx = d.vy = 0; }
    if (speed > 6 && (!okX || !okY)) { v.hp -= speed * 1.5; v.bumped = 0.3; ev = 'hit'; if (v.hp <= 0) { v.hp = 0; v.wrecked = true; d.mode = 'flat'; } }
  }
  v.speed = Math.hypot(d.vx, d.vy);
  // the picture: the nose turns to where it goes
  if (v.speed > 0.6) { const want = Math.atan2(d.vx, -d.vy); let da = want - v.angle; while (da > Math.PI) da -= Math.PI * 2; while (da < -Math.PI) da += Math.PI * 2; v.angle += Math.sign(da) * Math.min(Math.abs(da), st.turnRate * dt); if (v.angle > Math.PI) v.angle -= Math.PI * 2; else if (v.angle < -Math.PI) v.angle += Math.PI * 2; }
  if (v.bumped > 0) v.bumped -= dt;
  // down: on the ground (or in the water)
  if (v.alt <= 0.02 && wz <= 0 && floor <= 0) { v.alt = 0; d.vx = d.vy = 0; v.speed = 0; return isWater(w, v.x, v.y) ? 'water' : 'landed'; }
  return ev;
}

/** the item that flies as this drone: the first with `flies` naming it (content), else the drone's own id */
export function droneItemOf(items: { id: string; flies?: string }[], vehicleId: string): string { return items.find((it) => it.flies === vehicleId)?.id ?? vehicleId; }
/** my drone in the air, from a list of vehicles */
export function droneOf<V extends { drone?: { of: number } }>(vehicles: readonly V[], personId: number): V | undefined { for (const v of vehicles) if (v.drone?.of === personId) return v; return undefined; }
